/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import { translate } from 'react-i18next';

import AutoCompleteModel from 'models/forms/AutoCompleteModel';
import {
  autoCompleteManufacturersForAccount,
  autoCompleteAssetRequestCompanySites,
  autoCompleteAssetRequestAccountProject,
  autoCompleteAssetRequestDefaultAddress,
  autoCompleteRushAssetRequestAccountCompanies,
  autoCompleteAssetRequestAccountCostCenter,
  autoCompleteTradingAccountsForRushRequestAssetForm,
} from 'api/forms';

import FormControls from 'forms/components/controls/FormControls/FormControls';
import ElectroCheckbox from 'forms/components/inputs/ElectroCheckbox/ElectroCheckbox';
import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import ElectroSelectExtra from 'forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import AddOrRemoveButtonControl from 'forms/components/controls/FormControls/AddOrRemoveButtonControl';

import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import RushAssetDetailSection from './components/RushAssetDetailSection';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ActionDrawerWrapper from '../../components/ActionDrawerWrapper/ActionDrawerWrapper';

import {
  AddNewSite,
  AddNewCompany,
  AddNewProject,
  AddNewCostCenter,
} from '../../views/ActionDrawer';
import {
  genericActionList,
  customerDetailsActionList,
  deliveryDetailsActionList,
} from './utilities/actionDrawerActions';
import CatalogueProvider from './utilities/CatalogueContext/CatalogueProvider';
import addOneDay from '../../utils/addOneDay';

import { createAssetRequestForm, makeFields } from './setUpRushAssetRequestForm';

import styles from './RushAssetRequestForm.module.scss';

@translate()
@inject('actionDrawerStore', 'userStore')
@observer
class _RushAssetRequestForm extends React.Component<*> {
  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteTradingAccountsForRushRequestAssetForm
  );

  @observable manufacturerModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteManufacturersForAccount
  );

  @observable projectsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestAccountProject
  );

  @observable costCentreModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestAccountCostCenter
  );

  @observable customerDetailsCompanyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteRushAssetRequestAccountCompanies('customer')
  );

  @observable customerDetailsSiteModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestCompanySites('customer')
  );

  @observable defaultLocationModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestDefaultAddress
  );

  @observable deliveryDetailsCompanyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteRushAssetRequestAccountCompanies('delivery')
  );

  @observable deliveryDetailsSiteModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAssetRequestCompanySites('delivery')
  );

  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
    isDeliveryEqual: false,
    hasDefaultAddress: false,
    customerDetailsAddress: '',
    deliveryDetailsAddress: '',
  };

  /**
   * On mount, ensure at least one rush asset detail row exists.
   */
  componentDidMount(): void {
    this.form.$('rushAssetDetails').add({
      id: '',
      quantity: 0,
    });
  }

  /**
   * When a site changes, rebuild the address line.
   */
  handleSiteChangeHook(type: string) {
    return this.generateAddress(type);
  }

  /**
   * Sync "delivery equals customer" checkbox into delivery fields.
   */
  async onCheckboxChangeHook(form: Object, field: Object) {
    const values = form.values();
    const { customerDetails } = values;

    this.localState.isDeliveryEqual = field.value;

    if (field.value) {
      form.$('deliveryDetails.name').set('value', customerDetails.name);
      form.$('deliveryDetails.phoneNo').set('value', customerDetails.phoneNo);
      form.$('deliveryDetails.company').set('value', customerDetails.company);
      form.$('deliveryDetails.site').set('value', customerDetails.site);
      this.localState.deliveryDetailsAddress = this.localState.customerDetailsAddress;
    } else {
      form.$('deliveryDetails.name').set('value', '');
      form.$('deliveryDetails.phoneNo').set('value', '');
      form.$('deliveryDetails.company').set('value', '');
      form.$('deliveryDetails.site').set('value', '');
      this.localState.deliveryDetailsAddress = '';
    }
  }

  /**
   * When account changes, set default contact and attempt to prefill default location.
   */
  async onAccountChange(account) {
    this.setFieldDefaults();

    const location = await autoCompleteAssetRequestDefaultAddress('', account.id);
    const hasDefaultAddress = location.active.length > 0;

    if (hasDefaultAddress) {
      const defaultLocation = location.active[0];
      this.localState.hasDefaultAddress = true;
      this.localState.customerDetailsAddress = this.generateAddress('customerDetails')(
        defaultLocation
      );
      this.form.$('customerDetails.company').set('value', defaultLocation.company_name);
      this.form.$('customerDetails.site').set('value', defaultLocation.site_name);
      this.form.$('customerDetails.site').set('extra', {
        selectID: defaultLocation.id,
      });
    } else {
      this.localState.hasDefaultAddress = false;
    }
  }

  /**
   * Set initial values for user fields.
   */
  setFieldDefaults() {
    const { userStore } = this.props;
    const currentUser = userStore.activeUser;

    this.form.$('customerDetails.name').set('value', currentUser.full_name);
    this.form.$('customerDetails.phoneNo').set('value', currentUser.tel);
  }

  @computed get form(): Object {
    const { t } = this.props;
    const { labels, fields, rules, extra } = makeFields(t);

    return createAssetRequestForm(
      {
        labels,
        fields,
        rules,
        extra,
      },
      this.localState,
      t
    );
  }

  /**
   * Provide ActionDrawer components with the right context.
   * IMPORTANT: This is a Rush flow. We always pass rush flags and the location type
   * is derived from the drawer context: "customerDetails" => "customer", "deliveryDetails" => "delivery".
   */
  getActionDrawerData(form: Object, formName: string, fields: Object): React.Element<*> {
    const type = formName.split('Details')[0]; // "customer" | "delivery"

    return {
      [`${formName}NewCompany`]: (
        <AddNewCompany
          parentForm={form}
          fieldList={fields}
          accountsModel={this.accountsModel}
          model={this[`${formName}CompanyModel`]}
          isRushAssetRequest={true}
          rushAssetRequestLocation={type}
          isAssetRequest={false}
          locationType={null}
        />
      ),

      [`${formName}NewSite`]: (
        <AddNewSite
          parentForm={form}
          fieldList={fields}
          accountsModel={this.accountsModel}
          companyModel={this[`${formName}CompanyModel`]}
          model={this[`${formName}SiteModel`]}
          isRushAssetRequest={true}
          rushAssetRequestLocation={type}
          isAssetRequest={false}
          locationType={null}
        />
      ),
    };
  }

  /**
   * Compose a human-readable address and store it into localState.
   */
  generateAddress(type: string) {
    return (location: Object) => {
      const { address } = location;
      const fullAddress = [];

      for (let key in address) {
        if (key.includes('address') && address[key]) {
          fullAddress.push(address[key]);
        }
      }

      fullAddress.push(address.postcode);

      this.localState[`${type}Address`] = fullAddress.join(', ');

      return fullAddress.join(', ');
    };
  }

  /**
   * Required dates section with minimal validation constraints.
   */
  renderRequiredDatesSection(form: Object): React.Element<*> {
    const startDateField = form.$('requiredDates.startDate');
    const endDateField = form.$('requiredDates.endDate');

    const todayPlusOne = addOneDay();

    let startDatePlusOne = null;

    if (startDateField.value) {
      startDatePlusOne = addOneDay(startDateField.value);
    }

    return (
      <FormSection form={form} section="requiredDates">
        <ElectroDatePicker
          minDate={todayPlusOne}
          field={startDateField}
          clearDate={() => {
            startDateField.clear();
            endDateField.clear();
          }}
        />
        <ElectroDatePicker
          minDate={startDatePlusOne}
          field={endDateField}
          disabled={!startDateField.value}
        />
      </FormSection>
    );
  }

  /**
   * Additional information free text.
   */
  renderAdditionalInformationSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="additionalInformation">
        <ElectroTextField
          field={form.$('additionalInformation.details')}
          props={{
            multiline: true,
            rows: 5,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Customer details section.
   */
  renderCustomerDetailsSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="customerDetails">
        <ElectroTextField field={form.$('customerDetails.name')} />
        <ElectroTextField field={form.$('customerDetails.phoneNo')} />

        {this.renderCustomerDetailsDropdownPartial(form)}

        <div className={styles['address-row']}>{this.localState.customerDetailsAddress}</div>
        <ElectroCheckbox
          field={form.$('customerDetails.deliveryIsEqual')}
          onChange={this.onCheckboxChangeHook.bind(this, form)}
        />
      </FormSection>
    );
  }

  /**
   * Customer company/site dropdowns (+ action drawers) with default address behavior.
   */
  renderCustomerDetailsDropdownPartial(form): React.Element<*> {
    const actionsListData = customerDetailsActionList;
    const { hasDefaultAddress } = this.localState;

    const companyModel = this.customerDetailsCompanyModel;
    const siteModel = this.customerDetailsSiteModel;

    return (
      <>
        {this.localState.hasDefaultAddress ? (
          <>
            <ElectroTextField field={form.$('customerDetails.company')} disabled />
            <ElectroTextField field={form.$('customerDetails.site')} disabled />
          </>
        ) : (
          <>
            <ElectroSelectExtra
              field={form.$('customerDetails.company')}
              form={form}
              formName="customerDetails"
              labelKey="name"
              actionButtonData={{
                data: actionsListData[0],
                disabled:
                  hasDefaultAddress || !form.$('chooseAccount.tradingAccount').extra.selectID,
              }}
              props={{
                model: companyModel,
                modelChild: siteModel,
              }}
            />
            <ElectroSelectExtra
              field={form.$('customerDetails.site')}
              form={form}
              formName="site"
              labelKey="name"
              handleOnChange={this.handleSiteChangeHook('customerDetails')}
              actionButtonData={{
                data: actionsListData[1],
                disabled: hasDefaultAddress || !form.$('customerDetails.company').extra.selectID,
              }}
              props={{
                model: siteModel,
              }}
            />
          </>
        )}
      </>
    );
  }

  /**
   * Delivery details section.
   */
  renderDeliveryDetailsSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="deliveryDetails">
        <ElectroTextField field={form.$('deliveryDetails.name')} />
        <ElectroTextField field={form.$('deliveryDetails.phoneNo')} />

        {this.renderDeliveryDetailsDropdownPartial(form)}

        <div className={styles['address-row']}>{this.localState.deliveryDetailsAddress}</div>
        <ElectroTextField
          field={form.$('deliveryDetails.specialInstructions')}
          props={{
            multiline: true,
            rows: 5,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Delivery company/site dropdowns (+ action drawers) with "delivery equals" behavior.
   */
  renderDeliveryDetailsDropdownPartial(form): React.Element<*> {
    const actionsListData = deliveryDetailsActionList;
    const companyModel = this.deliveryDetailsCompanyModel;
    const siteModel = this.deliveryDetailsSiteModel;

    return (
      <>
        {this.localState.isDeliveryEqual && (
          <>
            <ElectroTextField field={form.$('deliveryDetails.company')} disabled />
            <ElectroTextField field={form.$('deliveryDetails.site')} disabled />
          </>
        )}

        {!this.localState.isDeliveryEqual && (
          <>
            <ElectroSelectExtra
              field={form.$('deliveryDetails.company')}
              form={form}
              formName="deliveryDetails"
              labelKey="name"
              actionButtonData={{
                data: actionsListData[0],
                disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
              }}
              props={{
                model: companyModel,
                modelChild: siteModel,
              }}
            />
            <ElectroSelectExtra
              field={form.$('deliveryDetails.site')}
              form={form}
              formName="site"
              labelKey="name"
              handleOnChange={this.handleSiteChangeHook('deliveryDetails')}
              actionButtonData={{
                data: actionsListData[1],
                disabled: !form.$('deliveryDetails.company').extra.selectID,
              }}
              props={{
                model: siteModel,
              }}
            />
          </>
        )}
      </>
    );
  }

  /**
   * Rush asset details (multiple rows).
   */
  renderRushAssetDetailsSection(form: Object): React.Element<*> {
    const detailsForm = form.$('rushAssetDetails');
    let canKeepAdding: boolean = true;
    if (detailsForm.value.length > 9) canKeepAdding = false;

    return (
      <FormSection form={form} section="rushAssetDetails" fluid>
        {this.renderRushAssetDetailPartial(form)}
        <AddOrRemoveButtonControl
          form={form}
          field={form.$('rushAssetDetails')}
          controls={{
            onAdd: canKeepAdding,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Render each rush asset detail row.
   */
  renderRushAssetDetailPartial(form: Object): React.Element<*> {
    const assets: Object = form.$('rushAssetDetails');

    return assets.map((field, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <CatalogueProvider key={index}>
        <RushAssetDetailSection form={form} field={field} index={index} />
      </CatalogueProvider>
    ));
  }

  /**
   * Trading account selection.
   */
  renderTradingAccountSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="chooseAccount">
        <ElectroSelectExtra
          field={form.$('chooseAccount.tradingAccount')}
          form={form}
          formName="chooseAccount"
          labelKey="name"
          handleOnChange={this.onAccountChange.bind(this)}
          props={{
            model: this.accountsModel,
            modelChild: [
              this.manufacturerModel,
              this.projectsModel,
              this.costCentreModel,
              this.customerDetailsCompanyModel,
              this.deliveryDetailsCompanyModel,
              this.defaultLocationModel,
            ],
          }}
        />
      </FormSection>
    );
  }

  /**
   * Assign projects section.
   */
  renderAssignProjectsSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="assignProjects">
        <ElectroSelectExtra
          field={form.$('assignProjects.list')}
          form={form}
          formName="assignProjects"
          labelKey={['code', 'name']}
          labelSeparator=" - "
          actionButtonData={{
            data: genericActionList[0],
            disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
          }}
          props={{
            model: this.projectsModel,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Assign cost centre section.
   */
  renderAssignCostCentreSection(form: Object): React.Element<*> {
    return (
      <FormSection form={form} section="assignCostCentre">
        <ElectroSelectExtra
          field={form.$('assignCostCentre.list')}
          form={form}
          formName="assignCostCentre"
          labelKey={['code', 'name']}
          labelSeparator=" - "
          actionButtonData={{
            data: genericActionList[1],
            disabled: !form.$('chooseAccount.tradingAccount').extra.selectID,
          }}
          props={{
            model: this.costCentreModel,
          }}
        />
      </FormSection>
    );
  }

  /**
   * Submit controls.
   */
  renderFormControlsSection(form: Object): React.Element<*> {
    return <FormControls buttonText="Submit Asset Request" form={form} />;
  }

  /**
   * Main render.
   */
  render(): React.Element<*> {
    const { userStore, actionDrawerStore } = this.props;
    const { form } = this;
    const currentUser: Object = userStore.getActiveUserFromLocal();

    return (
      <>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form} title="New Rush Asset Request">
            {this.renderTradingAccountSection(form)}
            {this.renderRushAssetDetailsSection(form)}
            {this.renderRequiredDatesSection(form)}
            {this.renderAdditionalInformationSection(form)}
            {this.renderCustomerDetailsSection(form)}
            {this.renderDeliveryDetailsSection(form)}
            {this.renderAssignProjectsSection(form)}
            {this.renderAssignCostCentreSection(form)}
            {this.renderFormControlsSection(form)}
          </FormWrapper>
        </PageWrapper>
        <ActionDrawerWrapper
          closeBackdropOnClick={false}
          onActionCompleted={() => {
            /* overriding with empty fn */
          }}
        >
          {
            {
              ...this.getActionDrawerData(form, 'customerDetails', {
                account: 'chooseAccount.tradingAccount',
                company: 'customerDetails.company',
                site: 'customerDetails.site',
              }),
              ...this.getActionDrawerData(form, 'deliveryDetails', {
                account: 'chooseAccount.tradingAccount',
                company: 'deliveryDetails.company',
                site: 'deliveryDetails.site',
              }),
              newProject: (
                <AddNewProject
                  parentForm={form}
                  accountsModel={this.accountsModel}
                  model={this.projectsModel}
                  localState={this.localState}
                  fieldList={{
                    account: 'chooseAccount.tradingAccount',
                    project: 'assignProjects.list',
                  }}
                  hideSections={['location']}
                  prefillFields={{
                    'addNewProject.projectStartDate': form.$('requiredDates.startDate'),
                    'addNewProject.projectEndDate': form.$('requiredDates.endDate'),
                    'addNewProject.chooseLead': {
                      value: `${currentUser.first_name} ${currentUser.last_name}`,
                      extra: {
                        selectID: currentUser.id,
                        prefillData: {
                          first_name: currentUser.first_name,
                          last_name: currentUser.last_name,
                          id: currentUser.id,
                        },
                      },
                    },
                    'addNewProject.projectEngineer': {
                      value: currentUser.id,
                      extra: {
                        selectID: currentUser.id,
                        prefillData: {
                          first_name: currentUser.first_name,
                          last_name: currentUser.last_name,
                          id: currentUser.id,
                        },
                      },
                    },
                    'location.locationCompany': this.localState.isDeliveryEqual
                      ? form.$('customerDetails.company')
                      : form.$('deliveryDetails.company'),
                    'location.locationSite': this.localState.isDeliveryEqual
                      ? form.$('customerDetails.site')
                      : form.$('deliveryDetails.site'),
                  }}
                  replaceModels={{
                    'location.locationCompany': this.localState.isDeliveryEqual
                      ? this.customerDetailsCompanyModel
                      : this.deliveryDetailsCompanyModel,
                    'location.locationSite': this.localState.isDeliveryEqual
                      ? this.customerDetailsSiteModel
                      : this.deliveryDetailsSiteModel,
                  }}
                />
              ),
              newCostCenter: (
                <AddNewCostCenter
                  parentForm={form}
                  accountsModel={this.accountsModel}
                  model={this.costCentreModel}
                  localState={this.localState}
                  fieldList={{
                    account: 'chooseAccount.tradingAccount',
                    costCentre: 'assignCostCentre.list',
                  }}
                />
              ),
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </>
    );
  }
}

export const RushAssetRequestForm = _RushAssetRequestForm;
